/**
 * Utilities for safe navigation in auth flows
 * Prevents redirect loops when canceling from login pages/modals
 */

// List of protected routes that require authentication
// Should match the list in App.tsx
const PROTECTED_ROUTES = ['/upgrade', '/my-collection'];

/**
 * Check if a pathname is a protected route
 */
export function isProtectedPath(pathname: string): boolean {
  // Remove query params and hash for comparison
  const cleanPath = pathname.split('?')[0].split('#')[0];
  return PROTECTED_ROUTES.includes(cleanPath);
}

/**
 * Get the last non-login path from sessionStorage
 */
export function getLastNonLoginPath(): string | null {
  return sessionStorage.getItem('ga:lastPath');
}

/**
 * Store the current path as the last non-login path (if it's not a login/auth route)
 */
export function updateLastNonLoginPath(pathname: string): void {
  // Don't store login or auth callback paths
  if (pathname !== '/login' && pathname !== '/auth/callback') {
    sessionStorage.setItem('ga:lastPath', pathname);
  }
}

/**
 * Determine a safe navigation target when canceling from login
 * @param locationState - The location.state?.from value (could be Location object or pathname string) or fromPath from modal config
 * @param isAuthenticated - Whether the user is currently authenticated
 * @returns The pathname to navigate to (guaranteed to not cause a redirect loop)
 */
export function getSafeCancelTarget(
  locationState?: { pathname?: string } | string | null,
  isAuthenticated: boolean = false
): string {
  // Priority 1: Use location.state.from or fromPath if present
  let targetPath: string | null = null;
  
  if (locationState) {
    if (typeof locationState === 'string') {
      targetPath = locationState;
    } else if (locationState.pathname) {
      targetPath = locationState.pathname;
    }
  }

  // Priority 2: Use sessionStorage last path
  if (!targetPath) {
    targetPath = getLastNonLoginPath();
  }

  // Priority 3: Default to home
  if (!targetPath) {
    return '/';
  }

  // Clean the path (remove query params for comparison)
  const cleanTarget = targetPath.split('?')[0].split('#')[0];

  // If target is protected and user is NOT authenticated, go to home instead
  if (isProtectedPath(cleanTarget) && !isAuthenticated) {
    return '/';
  }

  // If target is /login, go to home (avoid login loops)
  if (cleanTarget === '/login') {
    return '/';
  }

  return cleanTarget;
}

